import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RotateCw } from "lucide-react";
import { TopBar, DestinationRow, NumberTile, ActionButton, SecondaryAction, ListRow } from "./kit";
import FilterBox from "./FilterBox";
import { TapBoxButton, OUTLINE_RETRY_DRAWN_STYLE } from "./TapBoxButton";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { usePageWidth } from "../context/PageWidthContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import { withCreate } from "../lib/activityCreate";
import { counterText, NOT_RECORDED } from "../lib/counters";
import { avgText, bowlingAverage, economy, numberToWords, seasonBattingFigures, srText, toRateText } from "../lib/recordFigures";
import RecordOverdueCards from "./RecordOverdueCards";
import { ballsToOvers, leftOutNote, oversToBalls, unreadableOversCount } from "../lib/overs";

// counters.test.js's own repo-wide rule (#715): no `m.bowling?.x ?? 0` /
// `|| 0` literal anywhere in the tree, even for a SUM — every read goes
// through a named accessor first, so the zero-fallback a caller applies to
// its RETURN VALUE is never textually adjacent to the property access.
// (The batting equivalents, `runsOf`/`ballsFacedOf`, moved into
// `recordFigures.js`'s `seasonBattingFigures` with #945 — this screen no
// longer reads `m.batting` directly at all.)
const oversOf = (m) => m.bowling?.overs;
// #860: overs are overs and balls (3.4 is 22 balls), so the economy base is
// summed in balls. A spell whose overs are not over.ball notation has no
// balls and is left out of the economy; it still counts in every plain total.
const ballsOf = (m) => oversToBalls(oversOf(m));
const runsConcededOf = (m) => m.bowling?.runs_conceded;

// The figure tiles are drawn as white cards (round-10 RecordMatch board), not the
// page-grey `tile` default.
const TILE = { padding: 10, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)" };

/** "once"/"twice"/"N times" — `SeasonRecord.dc.html:149`'s own wording for
 * the batting-average not-out clause (see `battingRestsOn`'s own comment). */
const timesWord = (n) => (n === 1 ? "once" : n === 2 ? "twice" : `${numberToWords(n)} times`);

/**
 * Issue #762 — "one record, not two stats areas", wave 3 slice 3. Behind
 * `newUi("season-record")`, replaces `/dashboard` for a player whose
 * account holds two or more matches (`lib/recordGate.js`'s
 * `computeSeasonRecordGate`, `Dashboard.jsx`'s own `showSeasonRecord`).
 * Matches `SeasonRecord.dc.html` (round 5, sha256 3cdfe28e…) — the approved
 * drawing is the contract; the issue text is ruled by it where the two
 * differ (brief-762.md).
 *
 * SAME "the rendered screen replaces the page" mechanism #760/#761 built
 * on: its own navy `TopBar`, its own `DestinationRow` (current="record" —
 * the row has no separate "season" chip; #760/#761 both reuse "record" for
 * the same reason).
 *
 * EVERY FIGURE IS DERIVED FROM `matches` (Dashboard.jsx's own, unfiltered
 * `GET /matches`), FILTERED HERE by season/competition/team — never from
 * `/stats/summary`. Two reasons: (1) that endpoint applies the CURRENT
 * filter server-side and has no raw balls-faced/overs/runs-conceded totals
 * to build Strike Rate/Economy from independently of its own pre-divided
 * fields (confirmed by reading `api/src/routes/statsCompute.ts` — see
 * `lib/recordGate.js`'s own header); (2) this screen owns its filter bar
 * (ruling 4) and must show its OWN "filtered, no matches" state (ruling 3)
 * rather than falling back to the old dashboard's copy, which a
 * server-filtered summary alone cannot distinguish from "account has never
 * had two matches" the way `matches.length` (always unfiltered) can.
 *
 * #1410 (D367.18): the fielding and keeping figures, the five deep-analytics folds and the
 * Competition and Team boxes that used to sit behind this screen's More now live once on the
 * Dashboard (`/insights`, `RecordDashboardScreen`, `DeeperLook`), and this screen no longer
 * reads `/analytics/career`. A Competition or Team filter chosen there still narrows the
 * figures here (same `nw_dash_*` keys), and is named and cleared by the line under Season.
 */
export default function SeasonRecordScreen({
  matches,
  matchesFailed = false,
  onRetryMatches,
  season,
  comp,
  team,
  setSeason,
  setComp,
  setTeam,
  seasonOptions,
}) {
  const nav = useNavigate();
  const { setActive: setChromeless } = useChromelessOverride();
  // #1431 (archetype C, D386): the wide column; the figures and the rest sit two-up from 1024.
  usePageWidth("wide");
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  useEffect(() => {
    setChromeless(true);
    setTextSizeRetired(true);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterActive = season !== "all" || comp !== "all" || team !== "all";

  // The season's own filtered match list — client-side, over the SAME
  // fields `/matches/{seasons,competitions,teams}` populate the dropdowns
  // from (`api/src/routes/matches.ts`: `season`, `competition`, `team`).
  const filteredMatches = useMemo(
    () =>
      (matches || []).filter(
        (m) =>
          (season === "all" || m.season === season) &&
          (comp === "all" || m.competition === comp) &&
          (team === "all" || m.team === team),
      ),
    [matches, season, comp, team],
  );

  const figures = useMemo(() => {
    const sum = (fn) => filteredMatches.reduce((acc, m) => acc + (fn(m) || 0), 0);
    // #945 — the batting figures (innings/not-outs/highest/avg/sr) are
    // `recordFigures.js`'s own `seasonBattingFigures`, the same
    // battedIn/notOutIn rule statsCompute.ts uses (#873) — so the tiles
    // here and the API-backed fold elsewhere on this screen can never
    // disagree again (#945 F1). Extracted (not inlined) so it is pinned
    // directly, by its own unit test, not just through this screen's
    // rendered numbers.
    const batting = seasonBattingFigures(filteredMatches);
    const totalWickets = sum((m) => m.bowling?.wickets);
    const rateBase = filteredMatches.filter((m) => ballsOf(m) !== null);
    const totalBalls = rateBase.reduce((acc, m) => acc + (ballsOf(m) || 0), 0);
    const totalOvers = ballsToOvers(totalBalls);
    const rateRunsConceded = rateBase.reduce((acc, m) => acc + (runsConcededOf(m) || 0), 0);
    const totalRunsConceded = sum(runsConcededOf);
    return {
      matches: filteredMatches.length,
      innings: batting.innings,
      notOuts: batting.notOuts,
      totalRuns: batting.totalRuns,
      highest: batting.highest,
      totalWickets,
      sr: batting.sr,
      avg: batting.avg,
      econ: economy({ runs_conceded: rateRunsConceded, overs: totalOvers }),
      bowlAvg: bowlingAverage({ runs_conceded: totalRunsConceded, wickets: totalWickets }),
      totalOvers,
      totalRunsConceded,
      oversLeftOut: unreadableOversCount(filteredMatches),
    };
  }, [filteredMatches]);

  // R1 (PR #880 review) — a matches-load failure is not a genuine "filter
  // matches nothing": `filteredMatches` reads `[]` either way, and without
  // this guard a failed fetch would show D138's filtered-empty card
  // (wrong reason) instead of this screen's own failure card.
  const filteredEmpty = !matchesFailed && filterActive && figures.matches === 0;

  // R1 — every figure derives from `matches` (this file's own header), so
  // a failed `GET /matches` makes ALL of them untrustworthy, not just the
  // ones a separate `/stats/summary` failure would have touched. `dash`
  // wraps an already-computed display string and swaps in the dash
  // instead — "the tiles below show a dash instead of a number" (the
  // drawn failed-to-load copy) is only true if every tile actually does.
  const dash = (text) => (matchesFailed ? NOT_RECORDED : text);
  const MATCHES_FAILED_REASON = "Your matches didn't load, so this can't be worked out yet.";
  const dashHelp = (text) => (matchesFailed ? MATCHES_FAILED_REASON : text);

  // Batting average — `SeasonRecord.dc.html:149` draws this in DIGITS
  // ("Rests on 9 dismissals in 11 innings…"), not the number-WORDS
  // `recordFigures.js`'s `battingAverage().restsOn` uses for every other
  // caller (`numberToWords`, per brief-761.md ruling 3 — OneInnings.dc.html's
  // own convention, not this screen's). Composed here in full, digits and
  // all, rather than reused — same "the screen composes its own full
  // sentence from the shared decision's primitives" precedent
  // `RecordOneMatchScreen.jsx`'s own `bowlingRestsOn` sets, one step
  // further: only `battingAverage()`'s VALUE and its one-innings-dismissed
  // special sentence (an exact drawn quote in ITS OWN artboard, reused
  // verbatim rather than re-composed) come from the shared helper.
  const battingRestsOn =
    figures.avg.value == null
      ? figures.avg.reason
      : figures.avg.hypotheticalNotOut != null
        ? figures.avg.restsOn // the one-innings-dismissed shape already carries its own complete sentence.
        : figures.notOuts > 0
          ? `Rests on ${figures.avg.dismissals} dismissal${figures.avg.dismissals === 1 ? "" : "s"} in ${figures.innings} inning${figures.innings === 1 ? "" : "s"}` +
            ` — you were not out ${timesWord(figures.notOuts)}, and a not-out innings is not divided by.`
          : `Rests on ${figures.avg.dismissals} dismissal${figures.avg.dismissals === 1 ? "" : "s"} in ${figures.innings} inning${figures.innings === 1 ? "" : "s"}.`;


  return (
    // `data-testid="season"` matches `SeasonRecord.dc.html:77`'s own
    // `data-dc-id="season"` on ITS outer container (structure-check's join
    // key) — same "the drawing's own root testid lands on the outer
    // wrapper, `dashboard` one level in" pattern `RecordOneMatchScreen.jsx`
    // and `RecordEmptyScreen.jsx` both already use.
    <div data-testid="season" style={{ color: "var(--nw-ink)" }}>
    <div data-testid="dashboard">
      <TopBar
        variant="white"
        // PR #887 review — this screen is player-only, so its home is
        // always /dashboard, same as #760/#761's own two screens
        // (RecordEmptyScreen.jsx/RecordOneMatchScreen.jsx).
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu hideTextSize surface="white" />
            {/* #780 (D140) — the drawn empty account circle; see AccountMenu.jsx's own docstring on the `empty` prop. */}
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="record-topbar"
      />
      <DestinationRow current="record" />

      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "22px 20px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="nw-eyebrow">Your record</div>
          <div data-focus-after-delete tabIndex={-1} style={{ outline: "none" }} className="nw-title-lg">This season</div>
        </div>

        {/* D219 / D233.3 (round-10 RecordMatch board): Season alone on the
            face. Competition and Team sit behind More with the rest. */}
        <div style={{ display: "flex" }}>
          <FilterBox testId="perf-filter-season" label="Season" value={season} onChange={setSeason} options={seasonOptions} allLabel="All seasons" />
        </div>
        {/* D254 Q4 — a filter hidden behind a closed More is still named, and
            one tap clears it. Shown whether More is open or closed. */}
        {(comp !== "all" || team !== "all") && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: -6 }}>
            <div className="nw-help" data-testid="record-filter-line">
              Showing {[season !== "all" ? season : null, comp !== "all" ? comp : null, team !== "all" ? team : null].filter(Boolean).join(" · ")}.
            </div>
            <SecondaryAction
              variant="link"
              data-testid="record-clear"
              onClick={() => { setSeason("all"); setComp("all"); setTeam("all"); }}
              style={{ fontSize: 13, color: "var(--nw-navy)", flexShrink: 0 }}
            >
              Clear filters
            </SecondaryAction>
          </div>
        )}

        {/* R1 (PR #880 review) — `GET /matches` failing is THIS screen's
            own failure, not `/stats/summary`'s (this file's own header:
            every figure derives from `matches`, never from `summary`).
            Shown whenever `matchesFailed`, filter state notwithstanding —
            the drawn card sits where the "career totals didn't load"
            state always has, above whatever content is still honest to
            show. The tiles below genuinely dash (via `dash`/`dashHelp`),
            which is what makes this copy true rather than aspirational. */}
        {matchesFailed && (
          <div
            data-testid="dash-summary-error"
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              padding: 14,
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "var(--nw-ink)" }}>Your career totals didn&#x27;t load</div>
              <div className="nw-help">
                Nothing has been lost — we couldn&#x27;t reach the server, so the tiles below show a dash instead of a number.
              </div>
            </div>
            {/* #1394 (D221 G4, D354): the 40px outlined button is drawn inside a transparent 44px tap box. */}
            <TapBoxButton data-testid="dash-summary-retry" onClick={onRetryMatches} drawnStyle={OUTLINE_RETRY_DRAWN_STYLE}>
              <RotateCw size={14} aria-hidden="true" />
              Try again
            </TapBoxButton>
          </div>
        )}

        {/* #1150 — the same card as Plan's Coming up, above the season figures
            (round-10 PlanMatchAfter). Its verb is navy: the one gold on this
            screen stays Log your next match. */}
        <RecordOverdueCards />

        {filteredEmpty ? (
          // Ruling 3 (D141.3, D138) — the filters stay reachable (behind More);
          // only this card swaps in for the figures and All matches.
          <div
            data-testid="statistics-empty"
            style={{
              padding: "24px 16px",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
              textAlign: "center",
              fontSize: 14,
              lineHeight: 1.5,
              color: "var(--nw-ink-muted)",
            }}
          >
            Nothing matches this filter. Clear it to see your whole record.
          </div>
        ) : (
          <div className="nw-wide-grid nw-wide-grid--top" data-testid="season-grid">
            {/* The eight figures, as drawn on the round-10 RecordMatch board:
                two 2-column grids of tiles. The `tile` variant has no rests-on
                line, so each rate carries its own `nw-help` under it. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
              <NumberTile variant="tile" label="Runs this season" value={dash(counterText(figures.totalRuns))} labelLines={2} style={{ ...TILE }} data-testid="season-runs" />
              <NumberTile variant="tile" label="Matches" value={dash(counterText(figures.matches))} labelLines={2} style={{ ...TILE }} data-testid="season-matches" />
              <NumberTile variant="tile" label="Innings" value={dash(counterText(figures.innings))} labelLines={2} style={{ ...TILE }} data-testid="season-innings" />
              <NumberTile variant="tile" label="Not out" value={dash(counterText(figures.notOuts))} labelLines={2} style={{ ...TILE }} data-testid="season-not-out" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <NumberTile variant="tile" label="Strike rate" value={dash(srText(figures.sr.value))} labelLines={2} style={{ ...TILE }} data-testid="season-strike-rate" />
                <div className="nw-help">{dashHelp(figures.sr.value != null ? figures.sr.restsOn : figures.sr.reason)}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <NumberTile variant="tile" label="Batting average" value={dash(figures.avg.value == null ? NOT_RECORDED : avgText(figures.avg.value))} labelLines={2} style={{ ...TILE }} data-testid="season-batting-avg" />
                <div className="nw-help">{dashHelp(battingRestsOn)}</div>
              </div>
              <NumberTile variant="tile" label="Wickets" value={dash(counterText(figures.totalWickets))} labelLines={2} style={{ ...TILE, alignSelf: "start" }} data-testid="season-wickets" />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <NumberTile variant="tile" label="Bowling average" value={dash(figures.bowlAvg.value == null ? NOT_RECORDED : avgText(figures.bowlAvg.value))} labelLines={2} style={{ ...TILE }} data-testid="season-bowling-avg" />
                <div className="nw-help">{dashHelp(
                  figures.bowlAvg.value != null
                    ? `Rests on ${figures.totalRunsConceded} runs conceded off ${figures.totalOvers} overs, for ${figures.totalWickets} wickets. Economy ${figures.econ.value != null ? toRateText(figures.econ.value) : NOT_RECORDED} on the same overs.${figures.oversLeftOut ? ` (${leftOutNote(figures.oversLeftOut)})` : ""}`
                    : figures.bowlAvg.reason,
                )}</div>
              </div>
            </div>

            {/* #1123 (D221 O3) — the way from the season figures to Your
                matches (`/performance`, where edit and delete live). The
                count follows the filter. */}
            <Link to="/performance" data-wide="full" style={{ display: "block", textDecoration: "none" }}>
              <ListRow
                data-testid="record-all-matches"
                name="All matches"
                figuresLine={`${filteredMatches.length} ${filterActive ? "matching" : "this season"}, newest first`}
                chevron
                style={{ minHeight: 54, width: "100%", padding: "4px 16px" }}
              />
            </Link>
          </div>
        )}

        <ActionButton data-testid="season-log-next" onClick={() => nav(withCreate("/performance"))} style={{ color: "var(--nw-ink)" }}>
          Log your next match
        </ActionButton>
      </div>
    </div>

    </div>
  );
}
